/**
 * Links dos clientes — regras puras, sem Firebase.
 *
 * Usado nos DOIS lados: a aba Links da equipe (criar/editar) e a página pública `/l/<apelido>`
 * (que roda sem o app). Por isso não importa nada além de JS puro: a página pública precisa
 * nascer pequena, e a validação de endereço tem de ser a MESMA na gravação e na abertura.
 *
 * Dois tipos:
 *   - `encurtador`: o apelido manda direto para um endereço (com validade opcional).
 *   - `pagina`: vitrine estilo Linktree — foto, nome, frase, ícones de redes e uma lista de
 *     BLOCOS (link, título, texto, vídeo, música, formulário de contato), com aparência própria.
 *
 * Firestore: `linksCurtos/{apelido}` (o id do documento É o apelido — garante que dois links
 * nunca dividam o mesmo endereço), `linksCurtos/{apelido}/contagem/{chave}` com `{ n }` e
 * `linksCurtos/{apelido}/contatos/{id}` com o que o visitante deixou no formulário.
 */

export const COLECAO_LINKS = 'linksCurtos';
export const SUBCOLECAO_CONTAGEM = 'contagem';
export const SUBCOLECAO_CONTATOS = 'contatos';
export const CHAVE_VISITAS = 'visitas';

export const APELIDO_MIN = 2;
export const APELIDO_MAX = 40;
export const MAX_BLOCOS = 40;
/** @deprecated nome antigo de MAX_BLOCOS */
export const MAX_BOTOES = MAX_BLOCOS;
export const MAX_SOCIAIS = 12;
export const FUSO = 'America/Sao_Paulo';

/** Apelidos que confundiriam com rotas e arquivos do próprio site. */
const RESERVADOS = new Set([
  'l', 'c', 'api', 'admin', 'login', 'assets', 'marca', 'index', 'links', 'portal', 'messa',
]);

/* ─────────────────────────── Apelido ─────────────────────────── */

/** "Dra. Ana Lúcia — Dermato" → "dra-ana-lucia-dermato" */
export function sugerirApelido(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, APELIDO_MAX)
    .replace(/-+$/g, '');
}

/**
 * Normaliza o que a pessoa digitou no campo de apelido, sem decidir se é válido. Endereço
 * inteiro colado («https://link.messamkt.com/dra-ana?utm=…») vira só o que vem depois da
 * última barra («dra-ana»), em vez de «httpslinkmessamktcomdra-ana».
 */
export function normalizarApelido(texto) {
  let t = String(texto || '').trim();
  if (t.includes('/')) {
    const partes = t.split(/[?#]/)[0].split('/').filter(Boolean);
    t = partes.length ? partes[partes.length - 1] : '';
  }
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, APELIDO_MAX);
}

/**
 * Número de um destino de WhatsApp como a pessoa escreve: «(11) 98888-7777» para celular e fixo
 * do Brasil; outros países ficam com «+» e os dígitos. '' quando não é WhatsApp. Puro.
 */
export function telefoneDoWhatsapp(url) {
  const w = whatsappDoDestino(normalizarDestino(url));
  if (!w || !w.numero) return '';
  return formatarTelefone(w.numero);
}

/** Dígitos de telefone → «(11) 98888-7777» (com ou sem 55 na frente). Puro. */
export function formatarTelefone(digitos) {
  let d = String(digitos || '').replace(/\D/g, '');
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d ? `+${d}` : '';
}

/**
 * Endereço livre a partir de uma sugestão: o mesmo cliente pode ter vários links (página,
 * encurtador do WhatsApp…), então quando a sugestão já existe tenta `-2`, `-3`…
 */
export function apelidoLivre(base, emUso = new Set()) {
  const b = sugerirApelido(base);
  if (!b) return '';
  if (!emUso.has(b)) return b;
  for (let i = 2; i < 100; i += 1) {
    const sufixo = `-${i}`;
    const c = `${b.slice(0, APELIDO_MAX - sufixo.length).replace(/-+$/, '')}${sufixo}`;
    if (!emUso.has(c)) return c;
  }
  return '';
}

/**
 * O endereço principal de cada cliente (o nome dele, «clinica-cuida») é da página de links
 * desse cliente. Link curto nunca usa, e a página de um cliente não pega o de outro.
 * @param {string} apelido
 * @param {{ tipo: string, clienteNome?: string, nomesDosClientes?: string[] }} ctx
 * @returns {string} mensagem de erro, ou ''.
 */
export function erroDeReserva(apelido, { tipo, clienteNome = '', nomesDosClientes = [] }) {
  const reservados = new Set(nomesDosClientes.map(sugerirApelido).filter(Boolean));
  if (!reservados.has(apelido)) return '';
  if (tipo === 'pagina' && apelido === sugerirApelido(clienteNome)) return '';
  return tipo === 'pagina'
    ? 'Este endereço é o principal de outro cliente. Escolha outro.'
    : 'Este endereço fica reservado para a página de links do cliente. Use outro, como o sugerido.';
}

/** @returns {string} mensagem de erro, ou '' quando o apelido serve. */
export function erroDoApelido(apelido) {
  const a = String(apelido || '');
  if (a.length < APELIDO_MIN) return `Use pelo menos ${APELIDO_MIN} caracteres.`;
  if (a.length > APELIDO_MAX) return `Use no máximo ${APELIDO_MAX} caracteres.`;
  if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(a)) {
    return 'Só letras sem acento, números e hífen, sem hífen nas pontas.';
  }
  if (RESERVADOS.has(a)) return 'Este endereço é do próprio sistema. Escolha outro.';
  return '';
}

/** Mesmo padrão das regras do Firestore (ver `firestore.rules`, `linksCurtos`). */
export const REGEX_APELIDO = /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/;

/* ─────────────────────────── Endereços ─────────────────────────── */

/**
 * Endereço de destino: aceita o que a pessoa cola no dia a dia («instagram.com/fulana»,
 * «wa.me/5511…», «(11) 99999-0000») e devolve um endereço seguro, ou '' se não der.
 * Nunca deixa passar `javascript:`, `data:` e afins — o destino vai para a página pública.
 */
export function normalizarDestino(texto) {
  // Número copiado do WhatsApp/contatos vem com espaço e hífen «especiais» (sem quebra, U+2011)
  // e marcas invisíveis de direção do texto: viram espaço e hífen comuns antes de qualquer teste.
  let s = String(texto || '')
    .replace(/[\u200b-\u200f\u202a-\u202e\u2060-\u2069\ufeff]/g, '')
    .replace(/[\u00a0\u2000-\u200a\u202f\u205f\u3000]/g, ' ')
    .replace(/[\u2010-\u2015\u2212\ufe63\uff0d]/g, '-')
    .trim();
  if (!s) return '';
  if (/^(mailto:|tel:)/i.test(s)) {
    return /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(s) || /^tel:\+?[0-9]{8,15}$/i.test(s) ? s : '';
  }
  // E-mail solto vira mailto.
  if (/^[^\s@/]+@[^\s@/]+\.[a-z]{2,}$/i.test(s)) return `mailto:${s}`;
  // Telefone solto (só dígitos e pontuação de telefone) vira WhatsApp — é o uso de 9 em 10 clínicas.
  if (/^[+(\d][\d\s().-]{7,}$/.test(s)) {
    let dig = s.replace(/\D/g, '');
    if (dig.length === 10 || dig.length === 11) dig = `55${dig}`;
    return dig.length >= 12 && dig.length <= 15 ? `https://wa.me/${dig}` : '';
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !/^https?:\/\//i.test(s)) return '';
  if (!/^https?:\/\//i.test(s)) s = `https://${s.replace(/^\/+/, '')}`;
  let u;
  try {
    u = new URL(s);
  } catch {
    return '';
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return '';
  if (!u.hostname.includes('.') || /\s/.test(u.hostname)) return '';
  return u.toString();
}

/** O destino abre uma conversa de WhatsApp? (wa.me ou api.whatsapp.com) */
export function ehWhatsapp(url) {
  try {
    const u = new URL(String(url || ''));
    return /^(wa\.me|api\.whatsapp\.com|(www\.)?whatsapp\.com)$/i.test(u.hostname);
  } catch {
    return false;
  }
}

export const MAX_MENSAGEM = 500;

/**
 * Endereço do botão com a mensagem pronta do WhatsApp (`?text=`). A mensagem é opcional: vazia
 * ou destino que não é WhatsApp devolve o endereço como está. Mensagem nova substitui a colada.
 */
export function destinoComMensagem(url, mensagem) {
  const msg = String(mensagem || '').trim().slice(0, MAX_MENSAGEM);
  if (!msg || !ehWhatsapp(url)) return String(url || '');
  const u = new URL(url);
  u.searchParams.set('text', msg);
  // Espaço como %20 (o «+» do URLSearchParams nem todo aplicativo de WhatsApp lê como espaço).
  return u.toString().replace(/\+/g, '%20');
}

/** Número e mensagem de um destino de WhatsApp (para editar), ou null se não for WhatsApp. */
export function whatsappDoDestino(url) {
  if (!ehWhatsapp(url)) return null;
  const u = new URL(url);
  const numero = (u.hostname === 'wa.me' ? u.pathname : (u.searchParams.get('phone') || '')).replace(/\D/g, '');
  return { numero, mensagem: u.searchParams.get('text') || '' };
}

/** Número com DDD (+ mensagem opcional) → endereço wa.me pronto, ou '' se o número não serve. */
export function destinoWhatsapp(numero, mensagem = '') {
  const base = normalizarDestino(String(numero || '').trim());
  if (!base || !ehWhatsapp(base)) return '';
  return destinoComMensagem(base, mensagem);
}

/** Código de cor digitado («#1A2B3C», «1a2b3c», «#abc») → «#1a2b3c», ou '' se não for cor. */
export function normalizarHex(texto) {
  const t = String(texto || '').trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{6}$/.test(t)) return `#${t}`;
  if (/^[0-9a-f]{3}$/.test(t)) return `#${t.split('').map((c) => c + c).join('')}`;
  return '';
}

/** A página pública confere de novo: só abre o que `normalizarDestino` aceitaria. */
export function destinoSeguro(url) {
  const s = String(url || '');
  return /^(https?:\/\/|mailto:|tel:)/i.test(s) ? s : '';
}

/** Imagem só por https (foto, miniatura, fundo). */
export function imagemSegura(url) {
  const s = String(url || '').trim();
  return /^https:\/\/[^\s"'()<>]+$/i.test(s) && s.length <= 2000 ? s : '';
}

/* ─────────────────────────── Redes sociais ─────────────────────────── */

/** Redes do ícone no topo/rodapé. `prefixo` monta o endereço quando a pessoa digita só o @. */
export const REDES = {
  instagram: { nome: 'Instagram', prefixo: 'https://instagram.com/', dominio: /instagram\.com/ },
  whatsapp: { nome: 'WhatsApp', prefixo: '', dominio: /(wa\.me|whatsapp\.com)/ },
  tiktok: { nome: 'TikTok', prefixo: 'https://www.tiktok.com/@', dominio: /tiktok\.com/ },
  youtube: { nome: 'YouTube', prefixo: 'https://youtube.com/@', dominio: /(youtube\.com|youtu\.be)/ },
  facebook: { nome: 'Facebook', prefixo: 'https://facebook.com/', dominio: /(facebook\.com|fb\.com)/ },
  linkedin: { nome: 'LinkedIn', prefixo: 'https://linkedin.com/in/', dominio: /linkedin\.com/ },
  x: { nome: 'X (Twitter)', prefixo: 'https://x.com/', dominio: /(x\.com|twitter\.com)/ },
  threads: { nome: 'Threads', prefixo: 'https://www.threads.net/@', dominio: /threads\.(net|com)/ },
  spotify: { nome: 'Spotify', prefixo: '', dominio: /spotify\.com/ },
  telegram: { nome: 'Telegram', prefixo: 'https://t.me/', dominio: /(t\.me|telegram\.)/ },
  pinterest: { nome: 'Pinterest', prefixo: 'https://pinterest.com/', dominio: /pinterest\./ },
  maps: { nome: 'Google Maps', prefixo: '', dominio: /(maps\.google|google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/ },
  email: { nome: 'E-mail', prefixo: '', dominio: /^mailto:/ },
  site: { nome: 'Site', prefixo: '', dominio: /.^/ },
};

/** Qual rede é este endereço (para escolher o ícone). */
export function detectarRede(url) {
  const s = String(url || '').toLowerCase();
  if (s.startsWith('mailto:')) return 'email';
  for (const [id, r] of Object.entries(REDES)) {
    if (id !== 'email' && id !== 'site' && r.dominio.test(s)) return id;
  }
  return 'site';
}

/** «@dra.ana» numa rede vira o endereço do perfil; o resto passa por `normalizarDestino`. */
export function normalizarRede(rede, texto) {
  const s = String(texto || '').trim();
  const r = REDES[rede];
  if (r && r.prefixo && /^@?[\w.-]{2,60}$/.test(s) && !s.includes('.com')) {
    return r.prefixo + s.replace(/^@/, '');
  }
  return normalizarDestino(s);
}

/* ─────────────────────────── Vídeo e música ─────────────────────────── */

/** Endereço de incorporação (iframe) para YouTube, Vimeo e Spotify, ou ''. */
export function enderecoIncorporado(url) {
  const s = String(url || '').trim();
  let m = s.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{11})/i);
  if (m) return `https://www.youtube-nocookie.com/embed/${m[1]}`;
  m = s.match(/vimeo\.com\/(?:video\/)?(\d{6,12})/i);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  m = s.match(/open\.spotify\.com\/(?:intl-[a-z]+\/)?(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]{10,40})/i);
  if (m) return `https://open.spotify.com/embed/${m[1].toLowerCase()}/${m[2]}`;
  return '';
}

/** Altura do player incorporado. */
export function alturaIncorporado(embed) {
  if (/spotify\.com\/embed\/(track|episode)/.test(embed)) return 152;
  if (/spotify\.com\/embed\//.test(embed)) return 352;
  return 0; // vídeo: proporção 16:9 pela folha
}

/* ─────────────────────────── Aparência ─────────────────────────── */

/** Fontes oferecidas (Google Fonts). `css` é a família; `google` o nome na URL. */
export const FONTES = {
  inter: { nome: 'Inter', css: "'Inter', system-ui, sans-serif", google: 'Inter:wght@400;600' },
  poppins: { nome: 'Poppins', css: "'Poppins', system-ui, sans-serif", google: 'Poppins:wght@400;600' },
  montserrat: { nome: 'Montserrat', css: "'Montserrat', system-ui, sans-serif", google: 'Montserrat:wght@400;600' },
  dmsans: { nome: 'DM Sans', css: "'DM Sans', system-ui, sans-serif", google: 'DM+Sans:wght@400;600' },
  playfair: { nome: 'Playfair Display', css: "'Playfair Display', Georgia, serif", google: 'Playfair+Display:wght@400;600' },
  lora: { nome: 'Lora', css: "'Lora', Georgia, serif", google: 'Lora:wght@400;600' },
  cormorant: { nome: 'Cormorant', css: "'Cormorant Garamond', Georgia, serif", google: 'Cormorant+Garamond:wght@500;700' },
  dmserif: { nome: 'DM Serif', css: "'DM Serif Display', Georgia, serif", google: 'DM+Serif+Display' },
};
export const FONTE_PADRAO = 'inter';

/**
 * Como a foto aparece no topo:
 *   - circulo:  redonda e pequena, nome embaixo (o clássico do Linktree);
 *   - destaque: capa de ponta a ponta, nome, frase e ícones em branco sobre o escurecido;
 *   - cartao:   capa de ponta a ponta com um cartão do nome sobreposto embaixo (editorial).
 * Sem foto, os três caem no redondo com a inicial.
 */
export const PERFIS = { circulo: 'Foto redonda', destaque: 'Foto em destaque', cartao: 'Foto com cartão' };
export const PERFIL_PADRAO = 'circulo';

/** Ícones das redes: só o desenho, ou dentro de um círculo cheio. */
export const ESTILOS_SOCIAIS = { simples: 'Simples', circulo: 'Em círculo' };

export const ESTILOS_BOTAO = {
  cheio: 'Preenchido',
  contorno: 'Contorno',
  sombra: 'Com sombra',
  suave: 'Suave',
  lista: 'Lista com seta',
};
export const FORMAS_BOTAO = { reto: 'Reto', arredondado: 'Arredondado', pilula: 'Pílula' };
export const RAIO_FORMA = { reto: '0px', arredondado: '14px', pilula: '999px' };

export const DESTAQUES = {
  nenhum: 'Sem animação',
  balancar: 'Balançar',
  pulsar: 'Pulsar',
  brilho: 'Brilho passando',
};

/**
 * Temas prontos. Cada um é uma aparência COMPLETA (cores, fonte, botão, cabeçalho e ícones);
 * escolher um tema sobrescreve tudo, e mexer em qualquer coisa depois vira «personalizado»
 * (como no Linktree). Os de capa pedem foto: sem ela, o cabeçalho cai no redondo.
 */
export const TEMAS = {
  capa: { nome: 'Capa', fundo: '#111111', texto: '#ffffff', botao: '#ffffff', botaoTexto: '#111111', estilo: 'cheio', forma: 'arredondado', fonte: 'montserrat', perfil: 'destaque', sociais: 'simples' },
  autora: { nome: 'Autora', fundo: '#3b2b27', texto: '#f6efe6', botao: '#f6efe6', botaoTexto: '#3b2b27', estilo: 'lista', forma: 'reto', fonte: 'lora', perfil: 'cartao', sociais: 'circulo' },
  clinica: { nome: 'Clínica', fundo: '#ffffff', texto: '#1f2a2e', botao: '#2f6f6a', botaoTexto: '#ffffff', estilo: 'cheio', forma: 'pilula', fonte: 'dmsans', perfil: 'destaque', sociais: 'circulo' },
  oliva: { nome: 'Oliva', fundo: '#5e6a56', texto: '#ffffff', botao: '#d4d6cc', botaoTexto: '#1d1f1a', estilo: 'cheio', forma: 'pilula', fonte: 'dmsans', perfil: 'circulo', sociais: 'simples' },
  grafite: { nome: 'Grafite', fundo: '#ffffff', texto: '#111111', botao: '#2b3135', botaoTexto: '#ffffff', estilo: 'cheio', forma: 'arredondado', fonte: 'inter', perfil: 'circulo', sociais: 'simples' },
  estudio: { nome: 'Estúdio', fundo: '#2a2a2a', fundo2: '#0f0f0f', degrade: true, texto: '#f2f2f2', botao: '#a8a6a0', botaoTexto: '#ffffff', estilo: 'cheio', forma: 'reto', fonte: 'dmsans', perfil: 'circulo', sociais: 'simples' },
  areia: { nome: 'Areia', fundo: '#ece3d6', texto: '#3b2f25', botao: '#3b2f25', botaoTexto: '#ece3d6', estilo: 'cheio', forma: 'arredondado', fonte: 'dmsans', perfil: 'cartao', sociais: 'simples' },
  salvia: { nome: 'Sálvia', fundo: '#dfe6dc', texto: '#24332a', botao: '#24332a', botaoTexto: '#eef2ec', estilo: 'cheio', forma: 'pilula', fonte: 'dmsans', perfil: 'circulo', sociais: 'circulo' },
  rose: { nome: 'Rosé', fundo: '#f3e4e1', texto: '#4a2a2b', botao: '#ffffff', botaoTexto: '#4a2a2b', estilo: 'sombra', forma: 'pilula', fonte: 'playfair', perfil: 'circulo', sociais: 'simples' },
  marinho: { nome: 'Marinho', fundo: '#13233a', texto: '#eef1f5', botao: '#eef1f5', botaoTexto: '#13233a', estilo: 'cheio', forma: 'arredondado', fonte: 'montserrat', perfil: 'destaque', sociais: 'simples' },
  editorial: { nome: 'Editorial', fundo: '#fbfaf7', texto: '#111111', botao: '#111111', botaoTexto: '#111111', estilo: 'lista', forma: 'reto', fonte: 'cormorant', perfil: 'cartao', sociais: 'simples' },
  aurora: { nome: 'Aurora', fundo: '#f6d5c8', fundo2: '#c9d6f2', degrade: true, texto: '#2b2333', botao: '#ffffff', botaoTexto: '#2b2333', estilo: 'suave', forma: 'pilula', fonte: 'poppins', perfil: 'circulo', sociais: 'circulo' },
  claro: { nome: 'Claro', fundo: '#f5f3ef', texto: '#1a1816', botao: '#ffffff', botaoTexto: '#1a1816', estilo: 'sombra', forma: 'arredondado', fonte: 'inter', perfil: 'circulo', sociais: 'simples' },
  escuro: { nome: 'Escuro', fundo: '#141312', texto: '#f5f3ef', botao: '#2a2725', botaoTexto: '#f5f3ef', estilo: 'cheio', forma: 'arredondado', fonte: 'inter', perfil: 'circulo', sociais: 'simples' },
};
export const TEMA_PADRAO = 'claro';

const HEX = /^#[0-9a-f]{6}$/i;
const hexOu = (v, alt) => (HEX.test(String(v || '')) ? String(v) : alt);

/**
 * Ajustes finos: tudo o que a pessoa pode mexer depois de escolher o tema. Cada número tem
 * mínimo e máximo (a página pública confere de novo) e um padrão que vem do tema.
 */
export const AJUSTES = {
  nomeTamanho: { min: 16, max: 40, padrao: 22 },
  bioTamanho: { min: 12, max: 20, padrao: 15 },
  capaAltura: { min: 50, max: 130, padrao: 100 }, // % da largura (100 = quadrada)
  capaFoco: { min: 0, max: 100, padrao: 25 }, // ponto vertical da foto que fica à vista
  capaEscurecer: { min: 0, max: 100, padrao: 70 },
  botaoRaio: { min: 0, max: 32, padrao: 14 },
  botaoBorda: { min: 0, max: 4, padrao: 2 },
  botaoAltura: { min: 44, max: 76, padrao: 56 },
  botaoEspaco: { min: 4, max: 28, padrao: 12 },
  botaoFonteTamanho: { min: 13, max: 19, padrao: 15 },
  iconeTamanho: { min: 18, max: 34, padrao: 22 },
};
export const SOMBRAS_BOTAO = { nenhuma: 'Sem sombra', suave: 'Suave', dura: 'Marcada' };
export const ALINHAMENTOS = { centro: 'Centro', esquerda: 'Esquerda' };

const RAIO_PADRAO = { reto: 0, arredondado: 14, pilula: 32 };

function limitar(v, nome) {
  const r = AJUSTES[nome];
  const n = Number(v);
  if (!Number.isFinite(n)) return r.padrao;
  return Math.min(r.max, Math.max(r.min, Math.round(n)));
}

/** Aparência gravada a partir de um tema pronto (com todos os ajustes no padrão do tema). */
export function aparenciaDoTema(id) {
  const t = TEMAS[id] || TEMAS[TEMA_PADRAO];
  const tema = TEMAS[id] ? id : TEMA_PADRAO;
  const lista = t.estilo === 'lista';
  return {
    tema,
    temaBase: tema,
    fundoTipo: t.degrade ? 'degrade' : 'cor',
    fundo: t.fundo,
    fundo2: t.fundo2 || t.fundo,
    fundoImagem: '',
    texto: t.texto,
    fonte: t.fonte,
    botaoEstilo: t.estilo,
    botaoForma: t.forma,
    botaoCor: t.botao,
    botaoTexto: t.botaoTexto,
    perfil: t.perfil || PERFIL_PADRAO,
    sociais: t.sociais || 'simples',
    // Ajustes finos
    nomeFonte: t.perfil === 'cartao' ? t.fonte : '',
    nomeTamanho: t.perfil === 'circulo' ? 20 : 24,
    nomeItalico: t.perfil === 'cartao',
    nomeCor: '',
    bioTamanho: t.perfil === 'cartao' ? 12 : 15,
    bioCaixaAlta: t.perfil === 'cartao',
    bioCor: '',
    capaAltura: t.perfil === 'destaque' ? 120 : 100,
    capaFoco: 25,
    capaEscurecer: 70,
    cartaoFundo: '',
    cartaoTexto: '',
    botaoRaio: RAIO_PADRAO[t.forma] ?? 14,
    botaoBorda: t.estilo === 'contorno' ? 2 : lista ? 1 : 0,
    botaoSombra: t.estilo === 'sombra' ? 'dura' : t.estilo === 'suave' ? 'suave' : 'nenhuma',
    botaoAltura: lista ? 48 : 56,
    botaoEspaco: lista ? 8 : 12,
    botaoFonteTamanho: lista ? 14 : 15,
    botaoAlinhamento: lista ? 'esquerda' : 'centro',
    iconeTamanho: 22,
    iconeCor: '',
  };
}

/** Normaliza a aparência (aceita o formato antigo: `tema` + `corBotao`, e páginas sem ajustes). */
export function normalizarAparencia(pagina) {
  const a = pagina?.aparencia;
  if (!a || typeof a !== 'object') {
    const base = aparenciaDoTema(pagina?.tema);
    if (HEX.test(String(pagina?.corBotao || ''))) {
      base.botaoCor = pagina.corBotao;
      base.botaoTexto = textoSobre(pagina.corBotao);
      base.tema = 'personalizado';
    }
    return base;
  }
  const temaBase = TEMAS[a.temaBase] ? a.temaBase : (TEMAS[a.tema] ? a.tema : TEMA_PADRAO);
  const base = aparenciaDoTema(temaBase);
  // Página gravada antes dos ajustes finos: o estilo e a forma escolhidos valem como ponto de
  // partida (raio, borda e sombra saem deles, não do tema).
  const estilo = ESTILOS_BOTAO[a.botaoEstilo] ? a.botaoEstilo : base.botaoEstilo;
  const forma = FORMAS_BOTAO[a.botaoForma] ? a.botaoForma : base.botaoForma;
  const derivado = aparenciaDoTema(temaBase);
  derivado.botaoRaio = RAIO_PADRAO[forma] ?? derivado.botaoRaio;
  derivado.botaoBorda = estilo === 'contorno' ? 2 : estilo === 'lista' ? 1 : 0;
  derivado.botaoSombra = estilo === 'sombra' ? 'dura' : estilo === 'suave' ? 'suave' : 'nenhuma';
  const num = (k) => (a[k] == null || a[k] === '' ? derivado[k] : limitar(a[k], k));
  const cor = (k) => (HEX.test(String(a[k] || '')) ? String(a[k]) : '');
  return {
    tema: a.tema === 'personalizado' || TEMAS[a.tema] ? a.tema : base.tema,
    temaBase,
    fundoTipo: ['cor', 'degrade', 'imagem'].includes(a.fundoTipo) ? a.fundoTipo : base.fundoTipo,
    fundo: hexOu(a.fundo, base.fundo),
    fundo2: hexOu(a.fundo2, base.fundo2),
    fundoImagem: imagemSegura(a.fundoImagem),
    texto: hexOu(a.texto, base.texto),
    fonte: FONTES[a.fonte] ? a.fonte : base.fonte,
    botaoEstilo: estilo,
    botaoForma: forma,
    botaoCor: hexOu(a.botaoCor, base.botaoCor),
    botaoTexto: hexOu(a.botaoTexto, base.botaoTexto),
    perfil: PERFIS[a.perfil] ? a.perfil : base.perfil,
    sociais: ESTILOS_SOCIAIS[a.sociais] ? a.sociais : base.sociais,
    nomeFonte: FONTES[a.nomeFonte] ? a.nomeFonte : (a.nomeFonte === '' ? '' : derivado.nomeFonte),
    nomeTamanho: num('nomeTamanho'),
    nomeItalico: typeof a.nomeItalico === 'boolean' ? a.nomeItalico : derivado.nomeItalico,
    nomeCor: cor('nomeCor'),
    bioTamanho: num('bioTamanho'),
    bioCaixaAlta: typeof a.bioCaixaAlta === 'boolean' ? a.bioCaixaAlta : derivado.bioCaixaAlta,
    bioCor: cor('bioCor'),
    capaAltura: num('capaAltura'),
    capaFoco: num('capaFoco'),
    capaEscurecer: num('capaEscurecer'),
    cartaoFundo: cor('cartaoFundo'),
    cartaoTexto: cor('cartaoTexto'),
    botaoRaio: num('botaoRaio'),
    botaoBorda: num('botaoBorda'),
    botaoSombra: SOMBRAS_BOTAO[a.botaoSombra] ? a.botaoSombra : derivado.botaoSombra,
    botaoAltura: num('botaoAltura'),
    botaoEspaco: num('botaoEspaco'),
    botaoFonteTamanho: num('botaoFonteTamanho'),
    botaoAlinhamento: ALINHAMENTOS[a.botaoAlinhamento] ? a.botaoAlinhamento : derivado.botaoAlinhamento,
    iconeTamanho: num('iconeTamanho'),
    iconeCor: cor('iconeCor'),
  };
}

/** Tema final em valores de CSS, pronto para a vitrine. */
export function resolverTema(pagina) {
  const a = normalizarAparencia(pagina);
  let fundoCss = a.fundo;
  if (a.fundoTipo === 'degrade') fundoCss = `linear-gradient(160deg, ${a.fundo} 0%, ${a.fundo2} 100%)`;
  if (a.fundoTipo === 'imagem' && a.fundoImagem) {
    fundoCss = `linear-gradient(rgba(0,0,0,.28), rgba(0,0,0,.28)), url("${a.fundoImagem}") center / cover no-repeat, ${a.fundo}`;
  }
  const semCaixa = a.botaoEstilo === 'contorno' || a.botaoEstilo === 'lista';
  const sombra = {
    nenhuma: 'none',
    suave: '0 6px 24px rgba(0,0,0,.12)',
    dura: `4px 4px 0 ${a.texto}`,
  }[a.botaoSombra];
  return {
    ...a,
    fundoCss,
    corBase: a.fundo,
    botao: semCaixa ? 'transparent' : a.botaoCor,
    botaoTextoFinal: semCaixa ? a.botaoCor : a.botaoTexto,
    botaoBorda: a.botaoBorda > 0 ? a.botaoCor : 'transparent',
    botaoBordaLargura: `${a.botaoEstilo === 'lista' ? 0 : a.botaoBorda}px`,
    botaoSombraCss: a.botaoEstilo === 'lista' ? 'none' : sombra,
    raio: `${a.botaoRaio}px`,
    fonteCss: FONTES[a.fonte].css,
    nomeFonteCss: FONTES[a.nomeFonte]?.css || FONTES[a.fonte].css,
    nomeCorFinal: a.nomeCor || '',
    bioCorFinal: a.bioCor || '',
    cartaoFundoFinal: a.cartaoFundo || a.texto,
    cartaoTextoFinal: a.cartaoTexto || a.fundo,
    iconeCorFinal: a.iconeCor || a.texto,
    // Compatibilidade com quem lia o formato antigo.
    botaoTextoAntigo: a.botaoTexto,
  };
}

/** Preto ou branco, o que tiver mais contraste sobre `hex`. */
export function textoSobre(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '#1a1816';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.4 ? '#1a1816' : '#ffffff';
}

/* ─────────────────────────── Blocos ─────────────────────────── */

export const TIPOS_BLOCO = {
  link: { nome: 'Link', desc: 'Botão que leva a um site, rede, WhatsApp ou e-mail.' },
  titulo: { nome: 'Título', desc: 'Separa a página em seções.' },
  texto: { nome: 'Texto', desc: 'Um parágrafo curto entre os botões.' },
  video: { nome: 'Vídeo', desc: 'YouTube ou Vimeo tocando na própria página.' },
  musica: { nome: 'Música ou podcast', desc: 'Player do Spotify na página.' },
  contato: { nome: 'Formulário de contato', desc: 'O visitante deixa nome, e-mail ou WhatsApp.' },
};

let _seq = 0;
/** Id curto de bloco (vira a chave `b_<id>` da contagem — o padrão está nas regras). */
export function novoIdBotao() {
  _seq = (_seq + 1) % 1000;
  return `${Date.now().toString(36)}${_seq.toString(36)}`.slice(-12);
}
export const novoIdBloco = novoIdBotao;

export function novoBloco(tipo = 'link') {
  const b = { id: novoIdBloco(), tipo, rotulo: '', url: '', ativo: true };
  if (tipo === 'link') Object.assign(b, { mensagem: '', miniatura: '', destaque: 'nenhum', inicio: '', fim: '', sensivel: false });
  if (tipo === 'contato') Object.assign(b, { rotulo: 'Fale com a gente', campos: ['nome', 'telefone'], agradecimento: 'Recebemos seu contato. Retornaremos em breve.' });
  if (tipo === 'titulo') b.rotulo = '';
  return b;
}

/**
 * Junta à página o que veio do Linktree (`functions/linktree.js`): os botões entram no FIM da
 * lista, na ordem de lá, sem repetir endereço que a página já tem; as redes entram nos ícones
 * (uma por rede); nome e frase só preenchem o que está vazio. Título de grupo cujos links já
 * existiam todos não entra. Puro.
 * @returns {{ form: object, botoes: number, redes: number }}
 */
export function juntarLinktree(form, lt) {
  const chave = (u) => (normalizarDestino(u) || String(u || '')).replace(/\/+$/, '').toLowerCase();
  // Botão em branco (o que a página nova traz para começar) não fica na frente dos importados.
  const blocosAtuais = (form.blocos || []).filter((b) => (b.tipo || 'link') !== 'link' || b.rotulo || b.url || b.modo);
  const jaTem = new Set(blocosAtuais.map((b) => chave(b.url)).filter(Boolean));
  const fila = [];
  for (const l of lt?.links || []) {
    if (l.tipo === 'titulo') { fila.push({ ...novoBloco('titulo'), rotulo: String(l.rotulo || '').slice(0, 80) }); continue; }
    const k = chave(l.url);
    if (!k || jaTem.has(k)) continue;
    jaTem.add(k);
    const video = l.tipo === 'video' && enderecoIncorporado(l.url);
    fila.push({ ...novoBloco(video ? 'video' : 'link'), rotulo: String(l.rotulo || '').slice(0, 80), url: normalizarDestino(l.url) || l.url });
  }
  // Título sem nenhum link novo depois dele (até o próximo título) sai.
  const novos = fila.filter((b, i) => b.tipo !== 'titulo' || (fila[i + 1] && fila[i + 1].tipo !== 'titulo'));
  const blocos = [...blocosAtuais, ...novos].slice(0, MAX_BLOCOS);

  const sociaisAtuais = form.sociais || [];
  const redesQueTem = new Set(sociaisAtuais.map((x) => x.rede));
  const sociaisNovas = [];
  for (const x of lt?.sociais || []) {
    const url = normalizarDestino(x.url);
    if (!url) continue;
    const rede = detectarRede(url);
    if (redesQueTem.has(rede) && rede !== 'site') continue;
    if (sociaisAtuais.concat(sociaisNovas).some((y) => chave(y.url) === chave(url))) continue;
    redesQueTem.add(rede);
    sociaisNovas.push({ id: novoIdBloco(), rede, url });
  }
  const sociais = [...sociaisAtuais, ...sociaisNovas].slice(0, MAX_SOCIAIS);

  return {
    form: {
      ...form,
      nome: form.nome || String(lt?.nome || '').replace(/^@/, '').slice(0, 80),
      bio: form.bio || String(lt?.bio || '').slice(0, 240),
      blocos,
      sociais,
    },
    botoes: Math.max(0, blocos.length - blocosAtuais.length),
    redes: sociais.length - sociaisAtuais.length,
  };
}

/** O bloco aparece agora? (ligado e dentro da janela de agendamento). */
export function blocoVisivelAgora(b, agora = Date.now()) {
  if (!b || b.ativo === false) return false;
  const ini = b.inicio ? Date.parse(b.inicio) : NaN;
  const fim = b.fim ? Date.parse(b.fim) : NaN;
  if (Number.isFinite(ini) && agora < ini) return false;
  if (Number.isFinite(fim) && agora >= fim) return false;
  return true;
}

/** Blocos da página, aceitando o formato antigo (`botoes`). */
export function blocosDaPagina(pagina) {
  if (Array.isArray(pagina?.blocos)) return pagina.blocos;
  if (Array.isArray(pagina?.botoes)) return pagina.botoes.map((b) => ({ tipo: 'link', ...b }));
  return [];
}

const CAMPOS_CONTATO = ['nome', 'email', 'telefone'];

function limparBloco(b) {
  const tipo = TIPOS_BLOCO[b.tipo] ? b.tipo : 'link';
  const id = /^[a-z0-9]{1,16}$/.test(String(b.id || '')) ? String(b.id) : novoIdBloco();
  const out = { id, tipo, rotulo: String(b.rotulo || '').trim().slice(0, tipo === 'texto' ? 600 : 80), ativo: b.ativo !== false };
  if (tipo === 'link' || tipo === 'video' || tipo === 'musica') {
    const bruto = String(b.url || '').trim().slice(0, 2000);
    out.url = normalizarDestino(bruto) || bruto;
  }
  if (tipo === 'link') {
    out.miniatura = imagemSegura(b.miniatura);
    out.destaque = DESTAQUES[b.destaque] ? b.destaque : 'nenhum';
    out.inicio = Number.isFinite(Date.parse(b.inicio)) ? new Date(b.inicio).toISOString() : '';
    out.fim = Number.isFinite(Date.parse(b.fim)) ? new Date(b.fim).toISOString() : '';
    out.sensivel = b.sensivel === true;
    out.mensagem = String(b.mensagem || '').trim().slice(0, MAX_MENSAGEM);
    // Botão criado como WhatsApp continua mostrando o campo de número mesmo antes de ter um.
    if (b.modo === 'whatsapp' && (!out.url || ehWhatsapp(normalizarDestino(out.url)))) out.modo = 'whatsapp';
  }
  if (tipo === 'contato') {
    const campos = (Array.isArray(b.campos) ? b.campos : []).filter((c) => CAMPOS_CONTATO.includes(c));
    out.campos = campos.length ? campos : ['nome', 'telefone'];
    out.agradecimento = String(b.agradecimento || '').trim().slice(0, 200);
  }
  return out;
}

/** Problema do bloco que impede ele de aparecer (para avisar no editor), ou ''. */
export function problemaDoBloco(b) {
  if (b.tipo === 'link') {
    if (!String(b.rotulo || '').trim()) return 'Falta o título do botão.';
    if (!normalizarDestino(b.url)) return 'Falta um endereço válido.';
  }
  if ((b.tipo === 'video' || b.tipo === 'musica') && !enderecoIncorporado(b.url)) {
    return b.tipo === 'video' ? 'Cole um link do YouTube ou Vimeo.' : 'Cole um link do Spotify.';
  }
  if ((b.tipo === 'titulo' || b.tipo === 'texto') && !String(b.rotulo || '').trim()) return 'Escreva o texto.';
  return '';
}

/* ─────────────────────────── Documento ─────────────────────────── */

/**
 * Prepara o documento para gravar: limpa, valida e devolve `{ doc, erros }`.
 * `erros` é uma lista de frases para a tela; com erro, nada é gravado.
 *
 * Página de links é TOLERANTE (salva sozinha enquanto a pessoa digita, como o Linktree): um
 * bloco incompleto é guardado e só não aparece no ar. Erro de verdade é só o endereço.
 */
export function montarDocumento(form) {
  const erros = [];
  const apelido = String(form.apelido || '');
  const eApelido = erroDoApelido(apelido);
  if (eApelido) erros.push(`Endereço: ${eApelido}`);
  const tipo = form.tipo === 'pagina' ? 'pagina' : 'encurtador';
  const doc = {
    apelido,
    tipo,
    titulo: String(form.titulo || '').trim().slice(0, 80),
    clienteId: String(form.clienteId || ''),
    clienteNome: String(form.clienteNome || '').trim().slice(0, 120),
    ativo: form.ativo !== false,
  };
  if (tipo === 'encurtador') {
    const destino = normalizarDestino(form.destino);
    if (!destino) erros.push('Destino: cole um endereço válido (site, Instagram, WhatsApp, e-mail).');
    doc.destino = destino;
    doc.expiraEm = Number.isFinite(Date.parse(form.expiraEm)) ? new Date(form.expiraEm).toISOString() : '';
    return { doc, erros };
  }
  const blocos = (form.blocos || form.botoes || []).slice(0, MAX_BLOCOS).map(limparBloco);
  const sociais = (form.sociais || [])
    .slice(0, MAX_SOCIAIS)
    .map((s) => {
      const rede = REDES[s.rede] ? s.rede : detectarRede(s.url);
      const bruto = String(s.url || '').trim().slice(0, 2000);
      const social = { id: String(s.id || novoIdBloco()), rede, url: normalizarRede(rede, bruto) || bruto };
      if (rede === 'whatsapp') social.mensagem = String(s.mensagem || '').trim().slice(0, MAX_MENSAGEM);
      return social;
    });
  const red = form.redirecionar || {};
  doc.pagina = {
    nome: String(form.nome || '').trim().slice(0, 80),
    bio: String(form.bio || '').trim().slice(0, 240),
    foto: imagemSegura(form.foto),
    aparencia: normalizarAparencia({ aparencia: form.aparencia }),
    sociais,
    sociaisPosicao: form.sociaisPosicao === 'rodape' ? 'rodape' : 'topo',
    blocos,
    // Formulários de contato no ar: as regras só aceitam contato vindo de um deles.
    contatoBlocos: blocos.filter((b) => b.tipo === 'contato' && b.ativo !== false).map((b) => b.id),
    sensivel: form.sensivel === true,
    seo: {
      titulo: String(form.seo?.titulo || '').trim().slice(0, 70),
      descricao: String(form.seo?.descricao || '').trim().slice(0, 160),
    },
    rastreio: {
      pixelMeta: /^\d{8,20}$/.test(String(form.rastreio?.pixelMeta || '').trim()) ? String(form.rastreio.pixelMeta).trim() : '',
      ga4: /^G-[A-Z0-9]{4,14}$/i.test(String(form.rastreio?.ga4 || '').trim()) ? String(form.rastreio.ga4).trim().toUpperCase() : '',
    },
    redirecionar: {
      ativo: red.ativo === true && !!normalizarDestino(red.url),
      url: normalizarDestino(red.url) || '',
      ate: Number.isFinite(Date.parse(red.ate)) ? new Date(red.ate).toISOString() : '',
    },
  };
  return { doc, erros };
}

/** ISO gravado → valor de `<input type="datetime-local">` no fuso de quem edita. */
export function isoParaCampo(iso) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Documento gravado → estado do formulário. */
export function documentoParaForm(d) {
  const p = d?.pagina || {};
  const blocos = blocosDaPagina(p).map((b) => ({
    ...novoBloco(b.tipo || 'link'),
    ...b,
    inicio: isoParaCampo(b.inicio),
    fim: isoParaCampo(b.fim),
  }));
  return {
    apelido: d?.apelido || '',
    tipo: d?.tipo === 'pagina' ? 'pagina' : 'encurtador',
    titulo: d?.titulo || '',
    clienteId: d?.clienteId || '',
    clienteNome: d?.clienteNome || '',
    ativo: d?.ativo !== false,
    destino: d?.destino || '',
    expiraEm: isoParaCampo(d?.expiraEm),
    nome: p.nome || '',
    bio: p.bio || '',
    foto: p.foto || '',
    aparencia: normalizarAparencia(p),
    sociais: Array.isArray(p.sociais) ? p.sociais.map((s) => ({ ...s })) : [],
    sociaisPosicao: p.sociaisPosicao === 'rodape' ? 'rodape' : 'topo',
    blocos: blocos.length ? blocos : [novoBloco('link')],
    sensivel: p.sensivel === true,
    seo: { titulo: p.seo?.titulo || '', descricao: p.seo?.descricao || '' },
    rastreio: { pixelMeta: p.rastreio?.pixelMeta || '', ga4: p.rastreio?.ga4 || '' },
    redirecionar: {
      ativo: p.redirecionar?.ativo === true,
      url: p.redirecionar?.url || '',
      ate: isoParaCampo(p.redirecionar?.ate),
    },
      // Resultado da checagem diária (só leitura: montarDocumento não grava de volta).
    saude: d?.saude || null,
  };
}

/**
 * Problema que a checagem diária achou neste lugar (`b_<id>`, `s_<rede>`, `destino`,
 * `redirecionar`), ou null. Só vale enquanto o endereço for o mesmo: corrigiu, o aviso some.
 */
export function problemaDaChecagem(saude, chave, urlAtual) {
  const q = (saude?.quebrados || []).find((x) => x.chave === chave);
  if (!q) return null;
  const atual = normalizarDestino(urlAtual) || String(urlAtual || '');
  const sem = (u) => String(u || '').replace(/\/+$/, '');
  return sem(q.url) === sem(atual) ? q : null;
}

/** Quantos destinos de um link gravado continuam quebrados (para o selo da lista). */
export function quebradosDoLink(doc) {
  const s = doc?.saude;
  if (!s?.quebrados?.length) return [];
  const atual = (chave) => {
    if (chave === 'destino') return doc.destino;
    if (chave === 'redirecionar') return doc.pagina?.redirecionar?.url;
    if (chave.startsWith('b_')) return (blocosDaPagina(doc.pagina).find((b) => `b_${b.id}` === chave) || {}).url;
    if (chave.startsWith('s_')) return ((doc.pagina?.sociais || []).find((x) => `s_${x.rede}` === chave) || {}).url;
    return '';
  };
  return s.quebrados.filter((q) => problemaDaChecagem(s, q.chave, atual(q.chave)));
}

/** Data da última checagem, «22/10» (Timestamp do Firestore, Date ou ISO). */
export function dataDaChecagem(saude) {
  const v = saude?.verificadoEm;
  const d = v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v.seconds ? v.seconds * 1000 : v) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: FUSO });
}

/** Endereço público. `origem` = `location.origin` de quem está vendo. */
export function enderecoPublico(origem, apelido) {
  return `${String(origem || '').replace(/\/+$/, '')}/l/${apelido}`;
}

/** Encurtador vencido? */
export function encurtadorVencido(doc, agora = Date.now()) {
  const t = Date.parse(doc?.expiraEm || '');
  return Number.isFinite(t) && agora >= t;
}

/** Redirecionamento temporário da página inteira está valendo? */
export function redirecionamentoAtivo(pagina, agora = Date.now()) {
  const r = pagina?.redirecionar;
  if (!r || r.ativo !== true || !destinoSeguro(r.url)) return '';
  const ate = Date.parse(r.ate || '');
  if (Number.isFinite(ate) && agora >= ate) return '';
  return r.url;
}

/* ─────────────────────────── Contagem ─────────────────────────── */

/** Dia no fuso de Brasília, `AAAAMMDD`. */
export function diaChave(agora = Date.now()) {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(agora));
  const v = (t) => p.find((x) => x.type === t).value;
  return `${v('year')}${v('month')}${v('day')}`;
}

export const ORIGENS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  google: 'Google',
  youtube: 'YouTube',
  direto: 'Direto ou desconhecido',
  outros: 'Outros sites',
};

/** De onde veio a visita: `utm_source` > navegador interno do app > referência > direto. */
export function origemDaVisita({ busca = '', referencia = '', agente = '' } = {}) {
  const utm = (String(busca).match(/[?&]utm_source=([^&#]+)/i) || [])[1];
  const casar = (s) => {
    const t = String(s || '').toLowerCase();
    if (/instagram|(^|\W)ig(\W|$)/.test(t)) return 'instagram';
    if (/fban|fbav|facebook|fb\.|(^|\W)fb(\W|$)/.test(t)) return 'facebook';
    if (/whatsapp|wa\.me/.test(t)) return 'whatsapp';
    if (/tiktok|musical_ly|bytedance/.test(t)) return 'tiktok';
    if (/youtube|youtu\.be/.test(t)) return 'youtube';
    if (/google\./.test(t) || t === 'google') return 'google';
    return '';
  };
  if (utm) return casar(decodeURIComponent(utm)) || 'outros';
  const app = casar(agente);
  if (app && app !== 'google') return app;
  if (referencia) return casar(referencia) || 'outros';
  return 'direto';
}

export function dispositivoDaVisita(agente = '') {
  return /mobi|android|iphone|ipad/i.test(String(agente)) ? 'celular' : 'computador';
}

/**
 * Robôs, pré-visualizadores de link (WhatsApp, Facebook, Telegram…) e navegadores automatizados
 * abrem a página sem ser gente. Eles não entram na contagem.
 */
const AGENTE_ROBO = /bot\b|bot\/|crawl|spider|slurp|facebookexternalhit|facebookcatalog|meta-externalagent|whatsapp\/|telegrambot|twitterbot|linkedinbot|slackbot|discordbot|skypeuripreview|embedly|preview|headless|lighthouse|pingdom|uptime|google-inspectiontool|bingpreview|yandex|baiduspider|python-requests|curl\/|wget/i;

/** Parâmetro que os botões «Abrir» da dash põem no endereço: visita da equipe não conta. */
export const PARAM_EQUIPE = 'equipe';

/** Esta abertura deve somar na contagem? */
export function deveContar({ agente = '', busca = '', automatizado = false, marcadoEquipe = false } = {}) {
  if (automatizado || marcadoEquipe) return false;
  if (new RegExp(`[?&]${PARAM_EQUIPE}=1(&|$)`).test(String(busca))) return false;
  return !AGENTE_ROBO.test(String(agente));
}

/** Endereço para a EQUIPE abrir (não conta visita nem clique). */
export function enderecoDaEquipe(url) {
  return `${url}${url.includes('?') ? '&' : '?'}${PARAM_EQUIPE}=1`;
}

/** Chaves que uma abertura soma. */
export function chavesDaVisita(ctx = {}, agora = Date.now()) {
  return [CHAVE_VISITAS, `d_${diaChave(agora)}`, `o_${origemDaVisita(ctx)}`, `dv_${dispositivoDaVisita(ctx.agente)}`];
}

/** Chaves que um clique num bloco soma. */
export function chavesDoClique(blocoId, agora = Date.now()) {
  return [`b_${blocoId}`, `c_${diaChave(agora)}`];
}

/** Mesmo padrão das regras (`contagem/{chave}`). */
export const REGEX_CHAVE_CONTAGEM = /^(visitas|b_[a-z0-9]{1,16}|[dc]_[0-9]{8}|o_(instagram|facebook|whatsapp|tiktok|google|youtube|direto|outros)|dv_(celular|computador))$/;

/**
 * Mapa da contagem → números para a tela de Estatísticas.
 * `dias` = quantos dias para trás na série (inclui hoje).
 */
export function resumirContagem(mapa, { dias = 30, agora = Date.now() } = {}) {
  const m = mapa || {};
  const serie = [];
  for (let i = dias - 1; i >= 0; i -= 1) {
    const t = agora - i * 86400000;
    const k = diaChave(t);
    serie.push({ dia: k, visitas: m[`d_${k}`] || 0, cliques: m[`c_${k}`] || 0 });
  }
  const somaPeriodo = (campo) => serie.reduce((s, x) => s + x[campo], 0);
  const origens = Object.entries(m)
    .filter(([k]) => k.startsWith('o_'))
    .map(([k, n]) => ({ id: k.slice(2), n }))
    .sort((a, b) => b.n - a.n);
  const dispositivos = Object.entries(m)
    .filter(([k]) => k.startsWith('dv_'))
    .map(([k, n]) => ({ id: k.slice(3), n }))
    .sort((a, b) => b.n - a.n);
  const cliquesTotal = Object.entries(m).filter(([k]) => k.startsWith('b_')).reduce((s, [, n]) => s + n, 0);
  return {
    visitasTotal: m[CHAVE_VISITAS] || 0,
    cliquesTotal,
    visitasPeriodo: somaPeriodo('visitas'),
    cliquesPeriodo: somaPeriodo('cliques'),
    serie,
    origens,
    dispositivos,
    porBloco: (id) => m[`b_${id}`] || 0,
  };
}

/* ─────────────────────────── Contatos ─────────────────────────── */

/** Valida o que o visitante mandou no formulário. @returns {{ dados, erro }} */
export function validarContato(entrada, campos) {
  const dados = {};
  for (const c of campos) {
    const v = String(entrada?.[c] || '').trim();
    if (c === 'nome') {
      if (v.length < 2) return { dados: null, erro: 'Escreva seu nome.' };
      dados.nome = v.slice(0, 80);
    }
    if (c === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return { dados: null, erro: 'Confira o e-mail.' };
      dados.email = v.slice(0, 120).toLowerCase();
    }
    if (c === 'telefone') {
      const dig = v.replace(/\D/g, '');
      if (dig.length < 10 || dig.length > 13) return { dados: null, erro: 'Confira o WhatsApp com DDD.' };
      dados.telefone = dig;
    }
  }
  return { dados, erro: '' };
}

/** Lista de contatos → CSV (separador `;`, que o Excel em português abre direto). */
export function contatosParaCsv(lista) {
  // Texto que começa com = + - @ (ou tab/enter) vira fórmula no Excel: um visitante poderia
  // mandar «=HYPERLINK(...)» no nome. O apóstrofo na frente faz a planilha mostrar como texto.
  const neutro = (v) => (/^[=+\-@\t\r]/.test(v) ? `'${v}` : v);
  const esc = (v) => `"${neutro(String(v ?? '')).replace(/"/g, '""')}"`;
  const linhas = [['Data', 'Nome', 'E-mail', 'WhatsApp', 'Formulário'].map(esc).join(';')];
  for (const c of lista) {
    const data = c.criadoEm ? new Date(c.criadoEm).toLocaleString('pt-BR', { timeZone: FUSO }) : '';
    linhas.push([data, c.nome, c.email, c.telefone, c.blocoRotulo].map(esc).join(';'));
  }
  return `\ufeff${linhas.join('\r\n')}`;
}

/* ─────────────────────────── Página nova ─────────────────────────── */

/**
 * Página nova já montada a partir do cadastro do cliente (um registro só: nada é redigitado).
 * Entra o que é PÚBLICO por natureza: nome, foto, especialidades, Instagram, site e o endereço
 * profissional. Telefone e e-mail do cadastro costumam ser pessoais do médico e ficam de fora:
 * o botão de WhatsApp nasce pedindo o número da recepção. A página nasce PAUSADA; quem cria
 * revisa e publica.
 */
export function paginaInicialDoCliente(cliente = {}) {
  const nome = String(cliente.name || cliente.nome || '').trim();
  const foto = imagemSegura(cliente.photo);
  const especialidades = String(cliente.especialidades || cliente.especialidade || '').trim();
  const insta = String(cliente.instagram || '').trim();
  const site = normalizarDestino(cliente.siteUrl || '');
  const endereco = String(cliente.endProfissional || '').trim();

  const sociais = [];
  if (insta) {
    const url = normalizarRede('instagram', insta.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '@').replace(/\/$/, ''));
    if (url) sociais.push({ id: novoIdBloco(), rede: 'instagram', url });
  }
  if (site) sociais.push({ id: novoIdBloco(), rede: 'site', url: site });

  const blocos = [{ ...novoBloco('link'), rotulo: 'Agendar consulta pelo WhatsApp', url: '', modo: 'whatsapp' }];
  if (endereco) {
    blocos.push({
      ...novoBloco('link'),
      rotulo: 'Como chegar ao consultório',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`,
    });
  }
  if (site) blocos.push({ ...novoBloco('link'), rotulo: 'Conheça o site', url: site });
  if (insta) {
    const u = sociais.find((s) => s.rede === 'instagram')?.url;
    if (u) blocos.push({ ...novoBloco('link'), rotulo: 'Instagram', url: u });
  }

  return {
    nome,
    foto,
    bio: especialidades.replace(/\s*[,;/]\s*/g, ' · ').slice(0, 240),
    sociais,
    blocos,
    aparencia: aparenciaDoTema(foto ? 'capa' : 'salvia'),
    ativo: false,
  };
}
